/*
 * <price-slider> — a dual-handle range slider for the price filter.
 *
 * It does not submit anything itself. It drives the two hidden Dawn number
 * inputs (price-range input[type="number"]) in the same .facets__display and
 * dispatches input/change on them, so Dawn's debounced AJAX submit runs as
 * usual. The number inputs remain the source of truth and the no-JS fallback.
 *
 * Self-initialising custom element, so it also works after Dawn re-renders the
 * facets over AJAX (the element is re-created and upgraded automatically).
 */
if (!customElements.get('price-slider')) {
  class PriceSlider extends HTMLElement {
    connectedCallback() {
      this.minRange = this.querySelector('.price-slider__input--min');
      this.maxRange = this.querySelector('.price-slider__input--max');
      this.fill = this.querySelector('.price-slider__fill');
      this.minLabel = this.querySelector('.price-slider__value-min');
      this.maxLabel = this.querySelector('.price-slider__value-max');

      const display = this.closest('.facets__display');
      const numbers = display ? display.querySelectorAll('price-range input') : [];
      this.minNumber = numbers[0];
      this.maxNumber = numbers[1];

      if (!this.minRange || !this.maxRange || !this.minNumber || !this.maxNumber) return;

      this.rangeMin = Number(this.dataset.min || 0);
      // The true (possibly decimal) upper bound is used for the label and the
      // "at max" check. The range inputs snap to integer steps, so their max is
      // the ceiling of it; the top handle still represents the real maximum.
      this.displayMax = Number(this.dataset.max || 100);
      this.rangeMax = Math.ceil(this.displayMax);
      this.step = Number(this.dataset.step || 1);
      this.symbol = this.dataset.symbol || '';
      this.decimal = this.dataset.decimal || '.';

      [this.minRange, this.maxRange].forEach((input) => {
        input.min = this.rangeMin;
        input.max = this.rangeMax;
        input.step = this.step;
      });

      // Seed the handles from the (possibly empty) number inputs.
      const startMin = this.minNumber.value !== '' ? Number(this.minNumber.value) : this.rangeMin;
      const startMax = this.maxNumber.value !== '' ? Number(this.maxNumber.value) : this.rangeMax;
      this.minRange.value = startMin;
      this.maxRange.value = startMax;

      this.minRange.addEventListener('input', () => this.onInput('min'));
      this.maxRange.addEventListener('input', () => this.onInput('max'));
      this.minRange.addEventListener('change', () => this.commit());
      this.maxRange.addEventListener('change', () => this.commit());

      // The two inputs overlap, so the one painted on top would always win the
      // pointer. Before a drag starts, bring the handle nearest the pointer to
      // the front so both handles stay grabbable across the whole track.
      const track = this.querySelector('.price-slider__track');
      if (track) track.addEventListener('pointerdown', (event) => this.selectNearest(event));

      this.render();
    }

    selectNearest(event) {
      const track = this.querySelector('.price-slider__track');
      const rect = track.getBoundingClientRect();
      const span = this.rangeMax - this.rangeMin || 1;
      const pointerValue = this.rangeMin + ((event.clientX - rect.left) / rect.width) * span;
      const nearMin = Math.abs(pointerValue - Number(this.minRange.value));
      const nearMax = Math.abs(pointerValue - Number(this.maxRange.value));
      const minOnTop = nearMin <= nearMax;
      this.minRange.style.zIndex = minOnTop ? '2' : '1';
      this.maxRange.style.zIndex = minOnTop ? '1' : '2';
    }

    // Keep the handles from crossing while dragging.
    onInput(which) {
      let lo = Number(this.minRange.value);
      let hi = Number(this.maxRange.value);
      if (lo > hi - this.step) {
        if (which === 'min') {
          lo = hi - this.step;
          this.minRange.value = lo;
        } else {
          hi = lo + this.step;
          this.maxRange.value = hi;
        }
      }
      this.render();
    }

    // On release, write to the number inputs and let Dawn submit. Leave a bound
    // blank when it sits at the extreme so the filter param is omitted.
    commit() {
      const lo = Number(this.minRange.value);
      const hi = Number(this.maxRange.value);
      this.setNumber(this.minNumber, lo <= this.rangeMin ? '' : lo);
      this.setNumber(this.maxNumber, hi >= this.rangeMax ? '' : hi);
    }

    setNumber(input, value) {
      const next = value === '' ? '' : String(value);
      // Only fire events when the value actually changed. Dispatching change on
      // an untouched (blank) bound makes Dawn's PriceRange clamp it to the other
      // handle's value — e.g. dragging min to 21 would force max to 21 too.
      if (input.value === next) return;
      // When clearing a bound, reset its min attribute first. Dawn's
      // adjustToValidValues reads an empty value as 0 and would otherwise clamp
      // it up to the other handle (e.g. clearing max back to the ceiling while
      // min is 21 would force max to 21, giving 21 – 21).
      if (next === '') input.setAttribute('min', '0');
      input.value = next;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    render() {
      const lo = Number(this.minRange.value);
      const hi = Number(this.maxRange.value);
      const span = this.rangeMax - this.rangeMin || 1;
      const loPct = ((lo - this.rangeMin) / span) * 100;
      const hiPct = ((hi - this.rangeMin) / span) * 100;
      if (this.fill) {
        this.fill.style.left = loPct + '%';
        this.fill.style.right = 100 - hiPct + '%';
      }
      if (this.minLabel) this.minLabel.textContent = this.format(lo);
      // Show the true decimal maximum when the top handle is at its ceiling.
      const hiValue = hi >= this.rangeMax ? this.displayMax : hi;
      if (this.maxLabel) this.maxLabel.textContent = this.format(hiValue);
    }

    format(value) {
      const amount = Number(value).toFixed(2).replace('.', this.decimal);
      return this.symbol ? this.symbol + ' ' + amount : amount;
    }
  }

  customElements.define('price-slider', PriceSlider);
}
